import { EXTENSION_MIMETYPE, ToastStatus, useToastNotifications } from '@equifinance/shared';
import UploadFileArea from './UploadFileArea';

export const MAX_FILE_SIZE = +(process.env.REACT_APP_MAX_FILE_SIZE || '25') * 1000 * 1000; // default to 25MB

export const MAX_FILES_NUMBER = +(process.env.REACT_APP_MAX_FILES_NUMBER || '10'); // default to 10 files

export const ALLOWED_FILE_TYPES = (process.env.REACT_APP_ALLOWED_FILE_TYPES || 'PDF,PNG,EML,DOCX,XLSX,JPEG').split(',');

type UploadDocumentsProps = {
  supportedFileTypes?: string[],
  maxFileSize?: number, // in MB
  maxFilesNumber?: number,
  disabled?: boolean,
  uploadingFiles(files: File[]): Promise<void>;
}

export default function UploadDocuments({
  supportedFileTypes = ALLOWED_FILE_TYPES,
  maxFileSize = MAX_FILE_SIZE,
  maxFilesNumber = MAX_FILES_NUMBER,
  disabled = false,
  uploadingFiles,
}: UploadDocumentsProps) {
  const allowedFileTypes = supportedFileTypes.map((t) => t.trim().toLocaleUpperCase());
  const validFileMinetypes = allowedFileTypes.map((t) => EXTENSION_MIMETYPE[t]);

  const { addToastNotification } = useToastNotifications();

  const showToastMessage = (text: string, type: ToastStatus) => {
    addToastNotification({
      text,
      type,
      showCloseIcon: true,
    });
  };

  const filterValidFiles = (files: File[] = []): File[] => files.filter(
    (file) => (validFileMinetypes.includes(file.type) && file.size <= maxFileSize),
  );

  const filesToUpload = (files: File[] = []): File[] | null => {
    const numOfFiles = files.length;
    if (!numOfFiles) return null;

    const validFiles = filterValidFiles(files);
    const numOfValidFiles = validFiles.length;

    if (!numOfValidFiles) {
      showToastMessage('Invalid file(s) selected. No files uploaded', ToastStatus.ERROR);
      return null;
    }

    if (numOfValidFiles === numOfFiles && numOfValidFiles <= maxFilesNumber) {
      showToastMessage('Upload in progress', ToastStatus.SUCCESS);
      return validFiles;
    }

    const numberOfInvalidFiles = numOfFiles - numOfValidFiles;
    const numberOfExceededValidFiles = Math.max(numOfValidFiles - maxFilesNumber, 0);

    showToastMessage(`Upload in progress for valid files. ${numberOfInvalidFiles + numberOfExceededValidFiles} invalid file(s) excluded (please check file type, file size and number of files)`, ToastStatus.WARNING);
    return validFiles.slice(0, maxFilesNumber);
  };

  const handleUpload = async (fileList: FileList) => {
    const files = Array.from(fileList);

    try {
      const validFiles = filesToUpload(files);
      if (!validFiles || !validFiles.length) return;

      await uploadingFiles(validFiles);
      showToastMessage('Upload successful', ToastStatus.SUCCESS);
    } catch (error) {
      showToastMessage('Upload unsuccessful. Try again later. If the issue persists, contact Sales Support Team', ToastStatus.ERROR);
    }
  };

  return (
    <UploadFileArea
      supportedFileTypes={allowedFileTypes}
      onUploadFiles={handleUpload}
      disabled={disabled}
      maxFileSize={maxFileSize}
      maxFilesNumber={maxFilesNumber}
    />
  );
}
